import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, RefreshCw, X as XIcon, CalendarRange, Pencil, RefreshCcw, AlertCircle } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

/**
 * Admin queue for customer-submitted date-change requests.
 *
 * Data source: GET /admin/change-requests (reservations with
 * `change_status == "pending_admin"`). Each row shows the delta between the
 * original booking and the requested dates so the owner can decide at a
 * glance. Approve reuses admin_edit_reservation via
 * /admin/reservations/{id}/change-request/approve. Deny simply drops the
 * pending doc back off the reservation without touching totals.
 */
export const ChangeRequests = ({ embedded = false }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});
  const [denyModal, setDenyModal] = useState(null);
  const [denyNote, setDenyNote] = useState("");
  const [approveModal, setApproveModal] = useState(null); // { row, delta, label }
  const [approveNote, setApproveNote] = useState("");
  const [overrides, setOverrides] = useState({}); // {[res_id]: string}
  const [editingAmount, setEditingAmount] = useState({}); // {[res_id]: bool}

  const load = useCallback(() => {
    axios.get(`${API}/admin/change-requests`).then((r) => setRows(r.data.requests || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const retryCharge = async (r) => {
    if (!window.confirm(`Retry off-session charge of $${Number(r.pending_change_request?.delta || 0).toFixed(2)} on the customer's latest saved card?`)) return;
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/change-request/retry-charge`);
      const c = data?.charge || {};
      const label = c.card_brand ? `${c.card_brand.toUpperCase()} ••••${c.card_last4 || "????"}` : "saved card";
      toast.success(`Charged ${label} for $${Number(c.amount || 0).toFixed(2)} · date change applied ✨`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed — customer may need to update their card");
    } finally {
      setBusy((b) => ({ ...b, [r.id]: false }));
    }
  };

  const openApprove = (r) => {
    const req = r.pending_change_request || {};
    const autoDelta = Number(req.delta || 0);
    const overrideRaw = overrides[r.id];
    const overrideVal = overrideRaw !== undefined && overrideRaw !== "" ? Number(overrideRaw) : null;
    const delta = overrideVal !== null ? overrideVal : autoDelta;
    const label = delta > 0.005
      ? `Charge $${delta.toFixed(2)} extra`
      : delta < -0.005
        ? `Refund $${Math.abs(delta).toFixed(2)}`
        : "No price change";
    setApproveModal({ row: r, delta, label, overrideVal });
    setApproveNote("");
  };

  const submitApprove = async () => {
    if (!approveModal) return;
    const { row: r, delta, overrideVal } = approveModal;
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const body = { note: approveNote.trim() };
      if (overrideVal !== null) body.override_amount = overrideVal;
      const { data } = await axios.post(`${API}/admin/reservations/${r.id}/change-request/approve`, body);
      const c = data?.charge || {};
      const rf = data?.refund || {};
      if (data?.payment_required) {
        toast.info(`Emailed a Stripe payment link for $${Number(c.amount || 0).toFixed(2)} to ${r.customer_email} — change is pending until they pay.`);
      } else if (c.charged && c.method === "saved_card") {
        const cardLabel = c.card_brand ? `${c.card_brand.toUpperCase()} ••••${c.card_last4 || "????"}` : "saved card";
        toast.success(`Charged ${cardLabel} for $${Number(c.amount || 0).toFixed(2)} ✨`);
      } else if (rf.refunded) {
        toast.success(`Refunded $${Number(rf.amount || 0).toFixed(2)} to card ✨`);
      } else if (rf.error) {
        toast.error(`Change applied, but Stripe refund of $${Number(rf.amount || 0).toFixed(2)} failed — refund manually. (${rf.detail || "unknown error"})`);
      } else {
        toast.success("Date change approved ✨");
      }
      setApproveModal(null); setApproveNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setBusy((b) => ({ ...b, [r.id]: false }));
    }
  };
  const submitDeny = async () => {
    if (!denyModal) return;
    setBusy((b) => ({ ...b, [denyModal.id]: true }));
    try {
      await axios.post(`${API}/admin/reservations/${denyModal.id}/change-request/deny`, { note: denyNote });
      toast.success("Request denied.");
      setDenyModal(null); setDenyNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deny failed");
    } finally {
      setBusy((b) => ({ ...b, [denyModal.id]: false }));
    }
  };

  return (
    <div>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-4xl">Change Requests</h1>
          <Btn variant="ghost" onClick={load} data-testid="change-requests-reload"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end mb-4">
          <Btn variant="ghost" onClick={load} data-testid="change-requests-reload"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
        </div>
      )}
      {!rows ? <div className="text-[#B9C8D5]">Loading…</div> : rows.length === 0 ? (
        <Card className="p-10 text-center text-[#B9C8D5]" data-testid="change-requests-empty">
          🎉 No pending date-change requests. All caught up.
        </Card>
      ) : (
        <div className="space-y-4" data-testid="change-requests-list">
          {rows.map((r) => {
            const req = r.pending_change_request || {};
            const autoDelta = Number(req.delta || 0);
            const overrideRaw = overrides[r.id];
            const delta = (overrideRaw !== undefined && overrideRaw !== "") ? Number(overrideRaw) : autoDelta;
            const isCharge = delta > 0.005;
            const isRefund = delta < -0.005;
            return (
              <Card key={r.id} className="p-5" data-testid={`change-request-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">
                      Date change · #{r.id.slice(0, 8).toUpperCase()}
                    </div>
                    <div className="font-serif text-xl mt-1">{r.customer_name}</div>
                    <div className="text-sm text-[#B9C8D5]">
                      <a href={`mailto:${r.customer_email}`} className="hover:text-[#FFD166]">{r.customer_email}</a>
                      {r.customer_phone && <> · <a href={`tel:${r.customer_phone}`} className="hover:text-[#FFD166]">{r.customer_phone}</a></>}
                    </div>
                    <div className="text-xs text-[#B9C8D5] mt-1">
                      Requested {req.requested_at ? new Date(req.requested_at).toLocaleString() : "—"}
                      {r.delivery_resort ? ` · ${r.delivery_resort}` : ""}
                    </div>
                    {(req.item_swaps || []).length > 0 && (
                      <div className="mt-2 text-xs rounded-lg border border-[#FFD166]/30 bg-[#FFD166]/5 p-2" data-testid={`change-request-swaps-${r.id}`}>
                        <span className="text-[#FFD166] uppercase tracking-wider text-[10px]">Item replacement</span>
                        {req.item_swaps.map((sw, i) => <div key={i} className="text-[#F6F1E4]">{sw.from_name} → <strong>{sw.to_name}</strong> × {sw.quantity} (original unavailable on new dates)</div>)}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {isCharge && (
                      <>
                        <div className="font-serif text-3xl text-amber-300" data-testid={`change-request-delta-${r.id}`}>+${delta.toFixed(2)}</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">Extra charge if approved</div>
                      </>
                    )}
                    {isRefund && (
                      <>
                        <div className="font-serif text-3xl text-emerald-300" data-testid={`change-request-delta-${r.id}`}>−${Math.abs(delta).toFixed(2)}</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80">Refund if approved</div>
                      </>
                    )}
                    {!isCharge && !isRefund && (
                      <>
                        <div className="font-serif text-3xl text-[#FFD166]" data-testid={`change-request-delta-${r.id}`}>$0.00</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">No price change</div>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-4 p-3 rounded-lg border border-[#FFD166]/20 bg-black/25">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166] flex items-center gap-1">
                    <CalendarRange className="w-3 h-3" /> Dates
                  </div>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-[10px] text-[#B9C8D5]">Original</div>
                      <div className="text-[#F6F1E4]" data-testid={`change-request-old-${r.id}`}>
                        {req.original_delivery_date} → {req.original_pickup_date}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-[#B9C8D5]">Requested</div>
                      <div className="text-[#FFD166]" data-testid={`change-request-new-${r.id}`}>
                        {req.new_delivery_date} → {req.new_pickup_date}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="text-[#B9C8D5]">Old total: <span className="text-[#F6F1E4]">${Number(req.old_total || 0).toFixed(2)}</span></div>
                    <div className="text-[#B9C8D5]">New total: <span className="text-[#F6F1E4]">${Number(req.new_total || 0).toFixed(2)}</span></div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 items-center">
                  {/* Awaiting payment badge + retry */}
                  {req.payment_status === "awaiting_payment" && (
                    <div className="w-full p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 flex items-center gap-3 flex-wrap" data-testid={`change-request-awaiting-payment-${r.id}`}>
                      <AlertCircle className="w-4 h-4 text-amber-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-amber-200 font-medium">Awaiting payment</div>
                        <div className="text-[10px] text-amber-200/70">A Stripe payment link was emailed. Use "Retry charge" if the customer has since updated their card.</div>
                        {req.payment_link && (
                          <a href={req.payment_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#FFD166] hover:underline">View payment link →</a>
                        )}
                      </div>
                      <Btn onClick={() => retryCharge(r)} disabled={busy[r.id]} variant="ghost" className="text-xs" data-testid={`change-request-retry-${r.id}`}>
                        <RefreshCcw className="w-3 h-3" /> {busy[r.id] ? "…" : "Retry charge"}
                      </Btn>
                    </div>
                  )}
                  {/* Admin override amount (Phase 4) */}
                  <div className="flex items-center gap-2 mr-auto">
                    {editingAmount[r.id] ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#B9C8D5]">Override amount $</span>
                        <input
                          type="number"
                          step="0.01"
                          value={overrides[r.id] ?? ""}
                          placeholder={delta.toFixed(2)}
                          onChange={(e) => setOverrides((o) => ({ ...o, [r.id]: e.target.value }))}
                          className="w-24 bg-black/30 border border-[#FFD166]/30 text-[#F6F1E4] px-2 py-0.5 rounded text-sm"
                          data-testid={`change-request-override-input-${r.id}`}
                        />
                        <button className="text-xs text-[#B9C8D5] hover:text-[#FFD166]" onClick={() => { setEditingAmount((e) => ({ ...e, [r.id]: false })); setOverrides((o) => { const n = { ...o }; delete n[r.id]; return n; }); }}>reset</button>
                      </div>
                    ) : (
                      <button className="text-xs text-[#B9C8D5] hover:text-[#FFD166] flex items-center gap-1" onClick={() => setEditingAmount((e) => ({ ...e, [r.id]: true }))} data-testid={`change-request-override-toggle-${r.id}`}>
                        <Pencil className="w-3 h-3" /> Override charge amount
                      </button>
                    )}
                  </div>
                  <Btn onClick={() => openApprove(r)} disabled={busy[r.id]} data-testid={`change-request-approve-${r.id}`}>
                    <Check className="w-3.5 h-3.5" /> {busy[r.id] ? "…" : "Approve & apply change"}
                  </Btn>
                  <Btn variant="danger" onClick={() => { setDenyModal(r); setDenyNote(""); }} disabled={busy[r.id]} data-testid={`change-request-deny-${r.id}`}>
                    <XIcon className="w-3.5 h-3.5" /> Deny
                  </Btn>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {approveModal && (() => {
        const { row: r, delta, label } = approveModal;
        const req = r.pending_change_request || {};
        return (
          <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setApproveModal(null)}>
            <Card className="max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-400">Approve date change</div>
              <div className="font-serif text-xl mt-1">#{r.id.slice(0, 8).toUpperCase()}</div>
              <div className="mt-3 rounded-lg border border-[#FFD166]/20 bg-black/25 p-3 text-sm space-y-1">
                <div className="flex justify-between text-[#B9C8D5]">
                  <span>Original dates</span><span>{req.original_delivery_date} → {req.original_pickup_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#B9C8D5]">New dates</span><span className="text-[#FFD166] font-semibold">{req.new_delivery_date} → {req.new_pickup_date}</span>
                </div>
                <div className={`flex justify-between font-medium pt-1 border-t border-[#FFD166]/10 ${delta > 0.005 ? "text-amber-300" : delta < -0.005 ? "text-emerald-300" : "text-[#B9C8D5]"}`}>
                  <span>Price action</span><span>{label}</span>
                </div>
              </div>
              <p className="text-sm text-[#B9C8D5] mt-4">Add a note for the customer <span className="text-[#F6F1E4]/70">(optional)</span> — it will be included in the approval email sent to them.</p>
              <textarea value={approveNote} onChange={(e) => setApproveNote(e.target.value)} placeholder="e.g. All set! See you on the new dates. Feel free to reach out if you need anything else." className="mt-2 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm placeholder:text-[#B9C8D5]/40 resize-none" rows={3} data-testid="change-request-approve-note" />
              <div className="mt-4 flex gap-3">
                <Btn variant="ghost" onClick={() => setApproveModal(null)}>Back</Btn>
                <Btn onClick={submitApprove} data-testid="change-request-approve-confirm">
                  <Check className="w-3.5 h-3.5" /> Confirm approval
                </Btn>
              </div>
            </Card>
          </div>
        );
      })()}

      {denyModal && (
        <div className="fixed inset-0 z-[100] bg-[#040A0F]/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setDenyModal(null)}>
          <Card className="max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166]">Deny date change</div>
            <div className="font-serif text-xl mt-1">#{denyModal.id.slice(0, 8).toUpperCase()}</div>
            <p className="text-sm text-[#B9C8D5] mt-2">Add a reason for the customer <span className="text-[#F6F1E4]/70">(optional but recommended)</span> — it will be included in the denial email sent to them and logged to the reservation's activity history.</p>
            <textarea value={denyNote} onChange={(e) => setDenyNote(e.target.value)} placeholder="e.g. Sorry, those dates are fully booked. Your original reservation remains active." className="mt-3 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm placeholder:text-[#B9C8D5]/40" rows={3} data-testid="change-request-deny-note" />
            <div className="mt-4 flex gap-3">
              <Btn variant="ghost" onClick={() => setDenyModal(null)}>Back</Btn>
              <Btn variant="danger" onClick={submitDeny} data-testid="change-request-deny-confirm">Deny request</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
